import React, { useEffect, useState, useRef } from 'react'
import { Spin, message, Image, Skeleton, Modal, Button } from 'antd'
import CustomPagination from '../../components/common/CustomPagination'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import httpClient from '../../utils/HttpClient'
import { objectToQuery } from '../../utils/Utils'
import { FASHION_URL, GALLERY_URL } from '../../constants/Url'
import { useSelector } from 'react-redux'
import AuthModal from '../../components/auth/AuthModal'
import FashionModals from '../../components/FashionModals'
import PROModal from '../../components/PROModal'
import { useMemberAccess } from '../../hooks/useMemberAccess'

export default function FashionPage() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const authModalRef = useRef()
    const appDownloadRef = useRef()
    const user = useSelector((state) => state.authReducer)
    const { hasNoActiveMembership } = useMemberAccess()
    const [isMemberGateOpen, setIsMemberGateOpen] = useState(false)
    let token = user && user?.token ? user?.token : null
    const [searchParams] = useSearchParams()
    const initialSection = searchParams.get('section') || ''

    // Data States
    const [creativeList, setCreativeList] = useState([])
    const [creativeLoading, setCreativeLoading] = useState(true)
    const [total, setTotal] = useState(0)

    // Image Preview States
    const [previewOpen, setPreviewOpen] = useState(false)
    const [previewImage, setPreviewImage] = useState('')
    const [previewData, setPreviewData] = useState(null)

    // Pagination & Filter States
    const [pageNo, setPageNo] = useState(1)
    const [pageSize, setPageSize] = useState(9)
    const [activeTab, setActiveTab] = useState('男') // "男", "女", "前卫创意"
    const [fashion, setFashion] = useState([])
    const [fashionLoading, setFashionLoading] = useState(true)

    const fetchFashion = async () => {
        setFashionLoading(true)
        try {
            // isFeature: true
            const res = await httpClient.get(objectToQuery(FASHION_URL, { pageNo, pageSize, releaseYear: 'ALL' }))
                .then(res => res.data)
                .catch(error => ({ status: 500, message: error?.message }))

            if (res.status === 200) {
                setFashion([...res.data])
            } else {
                setFashion([])
            }
        } catch (error) {
            console.error('fetchFashion -> error', error)
        } finally {
            setFashionLoading(false)
        }
    }

    useEffect(() => {
        fetchFashion()
    }, [])

    // Fetch Avant-garde Creative
    const fetchCreative = async () => {
        setCreativeLoading(true)
        try {
            const query = { pageNo, pageSize, category: activeTab }
            const res = await httpClient.get(objectToQuery(GALLERY_URL, query))
                .then(res => res.data)
                .catch(() => null)

            if (res && res.status === 200 && res.data && res.data.length > 0) {
                setCreativeList(res.data)
                setTotal(res.total)
            } else {
                setCreativeList([])
                setTotal(0)
            }
        } catch (e) {
            setCreativeList([])
            setTotal(0)
        } finally {
            setCreativeLoading(false)
        }
    }

    useEffect(() => {
        fetchCreative()
    }, [pageNo, activeTab, pageSize])

    // Scroll to section based on URL query param
    useEffect(() => {
        if (initialSection) {
            setTimeout(() => {
                const element = document.getElementById(initialSection)
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
            }, 500)
        }
    }, [initialSection])

    const handleImageView = (url, item) => {
        if (!token) {
            authModalRef.current.open();
            return;
        }
        if (hasNoActiveMembership()) {
            setIsMemberGateOpen(true);
            return;
        }
        if (url) {
            setPreviewImage(url)
            setPreviewData(item)
            setPreviewOpen(true)
        }
    }

    const handleCardClick = (id) => {
        if (id && !id.startsWith('mock-') && !id.startsWith('m')) {
            if (hasNoActiveMembership()) {
                setIsMemberGateOpen(true);
                return;
            }
            navigate(`/fashion/${id}`)
        } else {
            message.info(t('text_viewMore_app'))
        }
    }

    const handleShowDownloadApp = (item) => {
        if (token) {
            if (user?.user?.completedTS) {
                if (hasNoActiveMembership()) {
                    setIsMemberGateOpen(true);
                    return;
                }
                appDownloadRef.current?.showModal(item)
            } else {
                authModalRef.current.open('REGISTER')
            }
        } else {
            authModalRef.current.open()
        }
    }

    return (
        <div className="w-full min-h-screen pb-16">
            <AuthModal ref={authModalRef} />

            <Modal
                open={previewOpen}
                onCancel={() => setPreviewOpen(false)}
                footer={null}
                centered
                width={600} // Give it a good width for images
            >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 0' }}>
                    {previewImage && (
                        <img 
                            src={previewImage} 
                            alt="Preview" 
                            style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: '5px' }} 
                        />
                    )}
                    {(previewData?.description || (previewData?.tags && previewData.tags.length > 0)) && (
                        <div style={{ 
                            color: '#333', 
                            marginTop: 20, 
                            fontSize: 16, 
                            fontWeight: 500,
                            lineHeight: 1.5,
                            width: '100%',
                        }}>
                            {previewData?.description && (
                                <div style={{ marginBottom: (previewData?.tags && previewData.tags.length > 0) ? '10px' : 0 }}>
                                    {previewData.description}
                                </div>
                            )}
                            {previewData?.tags?.length > 0 && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {previewData.tags.map((tag, index) => (
                                        <Button
                                            key={`${tag}-${index}`}
                                            type='text'
                                            size="small"
                                            className="tag-button !cursor-default !hover:bg-transparent"
                                            style={{ verticalAlign: 'baseline' }}
                                        >
                                            {tag}
                                        </Button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Modal>

            {/* ── Hero Banner ── */}
            <div className="bg-gradient-to-b from-[#f0f5ff] to-[#fbfcfd] py-8 sm:py-12 px-4 sm:px-8 md:px-16 lg:px-24">
                <div className="max-w-[1200px] mx-auto">
                    <div className="flex flex-wrap items-center gap-2 text-2xl sm:text-3xl md:text-4xl font-bold tracking-wider">
                        <span className="text-[#111]">{t('text_style')}</span>
                        <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 bg-clip-text text-transparent">
                            {t('text_inspiration')}
                        </span>
                    </div>
                    <p className="text-gray-500 text-[13px] sm:text-[14px] max-w-full sm:max-w-[500px] leading-relaxed mt-3">
                        {t(`fashion_text`)}
                    </p>
                </div>
            </div>

            {/* ── Main Content ── */}
            <div className="max-w-[1200px] mx-auto px-4 sm:px-6 md:px-4 lg:px-0 mt-6 sm:mt-8 flex flex-col gap-10 sm:gap-16">

                {/* ── Section 1: Theme works ── */}
                <section id="thematic" className="w-full">
                    <div className="flex justify-between items-center mb-4 sm:mb-6">
                        <h2 className="text-[17px] sm:text-[20px] font-bold text-gray-900 tracking-wide m-0">
                            {t('menu_theme')}
                        </h2>
                        <span
                            onClick={() => navigate('/fashion/detail', { state: { fromPrimary: true } })}
                            className="text-[12px] sm:text-[13px] text-gray-400 hover:text-black cursor-pointer font-medium transition-colors whitespace-nowrap"
                        >
                            {t('button_view_more')}
                        </span>
                    </div>

                    {fashionLoading ? (
                        <div className="flex flex-col md:flex-row overflow-hidden animate-pulse">
                            {/* Image Skeleton */}
                            <div className="w-full md:w-[45%] h-[220px] sm:h-[300px] md:h-[420px]">
                                <Skeleton.Image
                                    active
                                    className="!w-full !h-full"
                                    style={{ width: "100%", height: "100%" }}
                                />
                            </div>

                            {/* Content Skeleton */}
                            <div className="w-full md:w-[55%] p-5 sm:p-8 md:p-12 flex flex-col gap-4">
                                <Skeleton.Input
                                    active
                                    size="small"
                                    style={{ width: 120, height: 18 }}
                                />

                                <Skeleton.Input
                                    active
                                    size="large"
                                    style={{ width: "75%", height: 30 }}
                                />

                                <Skeleton
                                    active
                                    title={false}
                                    paragraph={{
                                        rows: 4,
                                        width: ["100%", "95%", "90%", "70%"],
                                    }}
                                />

                                <Skeleton.Button
                                    active
                                    shape="round"
                                    style={{
                                        width: 240,
                                        height: 48,
                                        marginTop: 16,
                                    }}
                                />
                            </div>
                        </div>
                    ) : (
                        fashion[0] && (
                            <div className="flex flex-col md:flex-row overflow-hidden">
                                {/* Image */}
                                <div className="w-full md:w-[45%] h-[220px] sm:h-[300px] md:h-[420px] overflow-hidden relative flex-shrink-0">
                                    <img
                                        src={fashion[0]?.picUrl}
                                        alt={fashion[0]?.title}
                                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 cursor-pointer"
                                        onClick={() => handleShowDownloadApp(fashion[0])}
                                    />
                                </div>

                                {/* Details */}
                                <div className="w-full md:w-[55%] p-5 sm:p-8 md:p-12 flex flex-col justify-center items-start gap-3">
                                    <span className="text-[12px] sm:text-[16px] font-semibold tracking-wider uppercase bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 bg-clip-text text-transparent">
                                        {(fashion[0]?.releaseYear ?? "")} {t('text_series')}
                                    </span>
                                    <h3
                                        className="
                        text-[24px] md:text-[28px]
                        font-bold
                        text-gray-950
                        leading-tight
                        mb-4
                        transition-all
                        duration-300
                        group-hover:bg-gradient-to-r
                        group-hover:from-pink-500
                        group-hover:via-purple-500
                        group-hover:to-blue-500
                        group-hover:bg-clip-text
                        group-hover:text-transparent
                      "
                                    >
                                        {fashion[0]?.title}
                                    </h3>

                                    <p className="text-gray-500 text-[13px] sm:text-[14px] leading-relaxed line-clamp-4 m-0">
                                        {fashion[0]?.description}
                                    </p>

                                    <div className="w-full sm:w-auto mt-5 p-[2px] bg-gradient-to-r from-[#50c7f8] via-[#7887fd] to-[#df97fb] rounded-md shadow-md">
                                        <button
                                            onClick={() => handleShowDownloadApp(fashion[0])}
                                            className="w-full sm:w-[240px] h-11 sm:h-12 flex items-center justify-center bg-gradient-to-r from-[#21163e] to-[#252554] text-white hover:opacity-90 transition-all rounded-md"
                                        >
                                            <span className="text-[12px] sm:text-[13px] tracking-widest text-center">
                                                {t('text_full_series')}
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )
                    )}
                </section>

                {/* ── Section 2: Avant-garde Creative ── */}
                <section id="creative" className="w-full">

                    {/* Section header — stacks gracefully on small screens */}
                    <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center border-b border-gray-100 pb-3 mb-6 sm:mb-8">

                        <h2 className="text-[17px] sm:text-[20px] font-bold text-gray-900 tracking-wide m-0">
                            {t('menu_creative')}
                        </h2>

                        {/* Tabs + View More row */}
                        <div className="flex items-start justify-between sm:justify-end gap-4 sm:gap-8">
                            {/* Tabs */}
                            <div className="flex gap-4 sm:gap-6 text-[13px] sm:text-[14px] font-medium overflow-x-auto">
                                {[
                                    {
                                        label: "text_male",
                                        value: "男"
                                    },
                                    {
                                        label: "text_female",
                                        value: "女"
                                    },
                                    {
                                        label: "text_creative",
                                        value: "前卫创意"
                                    },
                                ].map((tab) => (
                                    <div
                                        key={tab.value}
                                        onClick={() => { setActiveTab(tab.value); setPageNo(1); setPageSize(9); }}
                                        className={`cursor-pointer pb-2 relative transition-colors whitespace-nowrap ${activeTab === tab.value ? 'text-black font-semibold' : 'text-gray-400 hover:text-gray-600'
                                            }`}
                                    >
                                        {t(tab.label) || tab.value}
                                        {activeTab === tab.value && (
                                            <span className="absolute bottom-0 left-0 right-0 h-[3px] rounded-full" style={{ background: "linear-gradient(to right, #50c7f8, #7887fd, #df97fb)" }} />
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* View More */}
                            <span
                                onClick={() => navigate('/gallery/detail')}
                                className="text-[13px] sm:text-[14px] text-gray-400 hover:text-black cursor-pointer font-medium transition-colors whitespace-nowrap flex-shrink-0"
                            >
                                {t('button_view_more')}
                            </span>
                        </div>
                    </div>

                    {creativeLoading ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
                            {Array.from({ length: 6 }).map((_, index) => (
                                <Skeleton.Image
                                    key={index}
                                    active
                                    className="!w-full !h-[320px]"
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
                            {creativeList.map((item, index) => (
                                <div
                                    key={item.newsId || item._id || index}
                                    className="group cursor-pointer flex flex-col gap-2"
                                    onClick={() => handleImageView(item.images?.[0]?.url, item)}
                                >
                                    <div className="overflow-hidden rounded-lg shadow-sm group-hover:shadow-md transition-shadow duration-300" style={{ aspectRatio: "3/4" }}>
                                        <img
                                            src={item.images?.[0]?.url}
                                            alt={item.galleryId}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        />
                                    </div>
                                    {/* {item.tags && item.tags.length > 0 && (
                                        <div className="flex flex-wrap gap-1 px-1">
                                            {item.tags.map((tag, tagIdx) => (
                                                // <span key={tagIdx} className="text-[12px] text-[#e50a47] font-medium bg-[#fdecf1] px-2 py-0.5 rounded">
                                                //     {tag}
                                                // </span>
                                                <Button
                                                    key={tagIdx}
                                                    type='text'
                                                    className=" tag-button !cursor-default !hover:bg-transparent"
                                                >
                                                    {tag}
                                                </Button>
                                            ))}
                                        </div>
                                    )} */}
                                </div>
                            ))}
                        </div>
                    )}

                    {(creativeLoading || total > 0 || creativeList.length > 0) && (
                        <div className="flex justify-end mt-8 sm:mt-12">
                            <CustomPagination
                                current={pageNo}
                                pageSize={pageSize}
                                total={creativeLoading ? 9 : (total || creativeList.length || 0)}
                                pageSizeOptions={[9, 18, 27, 36, 45]}
                                onChange={(page, limit) => {
                                    if (limit !== pageSize) {
                                        setPageSize(limit)
                                        setPageNo(1)
                                    } else {
                                        setPageNo(page)
                                    }
                                }}
                            />
                        </div>
                    )}
                </section>

            </div>
            <FashionModals ref={appDownloadRef} />
            <PROModal open={isMemberGateOpen} onCancel={() => setIsMemberGateOpen(false)} />
        </div>
    )
}